import { Option, Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { addNewAddOns } from '../../hooks/use-get-default-values';
import {
  DocumentSaleAddOnsFormType,
  DocumentSaleGuestTypeFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetAddOnsTableList(parentIndex: number) {
  const { control } = useFormContext();
  const guestNameField =
    `documentSales.${parentIndex}.documentSaleGuests` as const;
  const { t } = useTranslation();
  const { editable } = useFormState();

  const [guests]: [DocumentSaleGuestTypeFormType[]] = useWatch({
    control,
    name: [guestNameField],
  });

  const guestList: Option[] = guests
    .filter((guest) => guest.guestId)
    .map((guest) => {
      return {
        id: guest.guestId,
        label: guest.guestName,
      };
    });

  const columns = React.useMemo(() => {
    return [
      {
        header: t('common:name'),
        accessorKey: 'guestId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: guestList,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 300,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, guestList, t]);

  return columns;
}

export default function AddOnsTable(props: Props) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { getValues, setValue, clearErrors } = useFormContext();

  const name = `documentSales.${props.parentIndex}.documentSaleAddOns` as const;

  const columns = useGetAddOnsTableList(props.parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex;
      const addOns = getValues(name);
      const addOn = addOns[currentRowIndex];
      const columnId = action.columnId as keyof DocumentSaleAddOnsFormType;
      const extra = action.extra as Value;

      const otherVal: () => Partial<DocumentSaleAddOnsFormType> = () => {
        if (columnId === 'guestId') {
          return { guestName: extra?.[0]?.label as string };
        }
        return {};
      };

      if (action.type === 'addRow') {
        const data = [...addOns, addNewAddOns()];
        setValue(name, data);
        clearErrors(name);
      } else if (action.type === 'removeRow') {
        addOns.splice(currentRowIndex, 1);
        setValue(name, addOns);
        clearErrors(name);
      } else if (action.type === 'update_cell') {
        addOns[currentRowIndex] = {
          ...addOn,
          [action.columnId]: action.value,
          ...otherVal(),
        };
        setValue(name, addOns);
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, name, setValue],
  );

  const addons = getValues(name);

  if (addons?.length || editable) {
    return (
      <div style={{ width: '100%' }}>
        <Text variant="ParagraphMedium">{t('ticketSales:add_ons')}</Text>
        <SeparatorVertical $margin={24} />
        <TableInput
          columns={columns}
          data={addons}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          uniqueRowKey="customId"
        />
      </div>
    );
  }
  return null;
}
